import {Component, OnInit} from '@angular/core';
import {Subscription} from 'rxjs';
import {ReportsFactory} from '../shared/factories/reports.factory';
import {MessageDisplayModel} from '../shared/message/messageDisplay.model';
import {Report} from '../shared/models/report.model';
import {ReportsList} from '../shared/models/reports-list.model';
import {MessageDisplayService} from '../shared/services/message-display.service';
import {ReportsService} from '../shared/services/reports.service';
import {SelectedLoanService} from '../shared/services/selected-loan.service';
import {DomSanitizer, Title} from '@angular/platform-browser';

@Component({
  selector: 'app-reports',
  styleUrls: ['./reports.component.scss'],
  templateUrl: './reports.component.html',
})
export class ReportsComponent implements OnInit {

  messageDisplay: MessageDisplayModel = new MessageDisplayModel();
  public reportPdfLink: any;

  public sortBy = ['Name', 'Type', 'Date Added'];
  private sortHeadings = ['FileName', 'FileType', 'DocumentDate'];
  public headings: Array<any> = [
    // {data: ''},
    {data: 'Name', class: 'one-four'},
    {data: 'Type', class: 'two-four'},
    {data: 'Date Added', class: 'four-four'},
  ];

  public iconList: string[] = [];
  public errorCountList: number[] = [];

  public reports: Array<Report>;
  public displayReports;

  public pageNumber = 1;
  public cursor = 0;
  public firstResult = 1;
  public lastResult: number;
  public lastPage: number;
  public totalResults: number;
  public isDoneGrabbing = false;
  public sortByString = 'Name';

  private selectedLoanSubscription: Subscription;
  public loanId: string;
  public pageLoading = true;

  // table stuff
  hasListeners = false;
  hasValues = false;
  internalCheckedState: any = {};

  constructor(private reportsService: ReportsService,
              private selectedLoanService: SelectedLoanService,
              private messageDisplayService: MessageDisplayService,
              public sanitizer: DomSanitizer,
              private titleService: Title) {
    this.selectedLoanSubscription = this.selectedLoanService.selectedLoanId$.subscribe((loanId: string) => {
      this.loanId = loanId;
    });
  }

  ngOnInit() {
    this.titleService.setTitle('Arvest Business Partner Portal: Reports');
    this.getReports();
  }

  public setSortBy(header: string) {
    const sortBy = this.sortHeadings[this.sortBy.indexOf(header)];
    this.reports = this.reports.sort((a, b) => a[sortBy].localeCompare(b[sortBy]));
    this.displayReports = ReportsFactory.formatReportsForTable(this.reports.slice(0, 29));
  }

  convertDataURIToBinary(base64) {
    const pdfData = atob(base64);
    const uInt8ArrayPdf = new Uint8Array(pdfData.length);

    for (let i = 0; i < pdfData.length; i++) {
      uInt8ArrayPdf[i] = pdfData.charCodeAt(i);
    }
    const blob = new Blob([uInt8ArrayPdf]);
    return blob;
  }

  public getReports() {
    if (this.messageDisplayService.hasMessage()) {
      this.messageDisplayService.clearMessage();
    }
    this.messageDisplay = null;
    for (let index = 0; index < 30; index++) {
      this.iconList[index] = '';
      this.errorCountList[index] = 0;
    }
    this.reportsService.getReports()
      .then((reports: ReportsList) => {
        this.reports = reports.Documents;
        console.log('Report', reports);
        this.totalResults = reports.Documents.length;
        this.lastResult = this.totalResults;
        this.cursor = parseInt(reports.Cursor);
        if (reports.MoreInformation !== 'End of Search') {
          this.keepGrabbing();
        } else {
          this.lastPage = Math.ceil(this.totalResults / 30);
          this.isDoneGrabbing = true;
          this.pageLoading = false;
        }
        this.setSortBy(this.sortByString);
      }).catch(error => {
      this.pageLoading = false;
      this.messageDisplay = this.messageDisplayService.createErrorMessageDisplay(
        'GENERIC_PROBLEM');
    });
  }

  async keepGrabbing() {
    while (this.cursor > this.totalResults - 1) {
      const newReports = await this.reportsService.getReports(this.cursor.toString());
      newReports.Documents.forEach(report => this.reports.push(report));
      this.totalResults = this.totalResults + newReports.Documents.length;
      this.cursor = parseInt(newReports.Cursor);
      console.log('Adding Reports', newReports);
      this.isDoneGrabbing = newReports.MoreInformation === 'End of Search';
      this.pageLoading = false;
      if (this.isDoneGrabbing) {
        this.lastPage = Math.ceil(this.totalResults / 30);
      }
    }
  }

  async loadReport(reportId: string, index) {
    if (this.messageDisplayService.hasMessage()) {
      this.messageDisplayService.clearMessage();
    }
    this.messageDisplay = this.messageDisplayService.createPassiveMessageDisplayWithGivenMessage('Preparing and Downloading Report');
    this.iconList[index] = 'spinner';
    const fileType = this.reports.filter(report => report.DocumentID === reportId)[0].FileType;
    const fileName = this.reports.filter(report => report.DocumentID === reportId)[0].FileName;
    await this.reportsService.getReportById(reportId).then(b64 => {
      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        this.reportPdfLink = window.navigator.msSaveOrOpenBlob(this.convertDataURIToBinary(b64['Document']), `${fileName}`);
      } else {
        const url = window.URL.createObjectURL(this.convertDataURIToBinary(b64['Document']));
        const a = document.createElement('a');
        a.href = url;
        a.download = `${fileName}`;
        document.body.appendChild(a);
        a.click();
        this.iconList[index] = 'check';
      }
      if (this.messageDisplayService.hasMessage()) {
        this.messageDisplayService.clearMessage();
      }
      this.messageDisplay = null;
    }).catch(error => {
      this.iconList[index] = 'priority_high';
      this.errorCountList[index]++;
      if (this.errorCountList[index] >= 3) {
        this.messageDisplay = this.messageDisplayService.createErrorMessageDisplay(
          'DOCUMENT_ERROR_MANY');
      } else {
        this.messageDisplay = this.messageDisplayService.createErrorMessageDisplay(
          'DOCUMENT_ERROR');
      }
    });
  }

  fetchNewRecords(page: number) {
    for (let index = 0; index < 30; index++) {
      this.iconList[index] = '';
    }
    this.pageNumber = page;
    const start = 30 * (page - 1);
    const end = start + 30 <= this.totalResults ? start + 29 : this.totalResults - 1;
    this.firstResult = start + 1;
    this.lastResult = end + 1;
    this.displayReports = ReportsFactory.formatReportsForTable(this.reports.slice(start, end));
  }

  public is_array(item) {
    return Array.isArray(item);
  }

  getIDs(row: number, data: any) {
    let rowPrefix = row.toString();
    rowPrefix += '-';

    if (this.is_array(data)) {
      let retVal = '';
      for (const item of data) {
        if (typeof item !== 'undefined' && typeof item.data !== 'undefined') {
          retVal += `${rowPrefix}${item.data.replace(/\s/g, '_')} `;
        }
      }
      return retVal;
    } else {
      if (typeof data === 'undefined') {
        return rowPrefix;
      } else {
        return `${rowPrefix}${data.data.replace(/\s/g, '_')}`;
      }
    }
  }

  fixSpaces(val: string) {
    if (typeof val === 'undefined') {
      return '';
    } else {
      return val.replace(/\s/g, '_');
    }
  }

  public clearCheckboxes() {
    this.internalCheckedState = {};
  }

  public rowClick(numericIndex: number, event: MouseEvent, row) {
    if (row[0].value) {
      this.loadReport(row[0].value, this.displayReports.indexOf(row));
    }
  }
}
